import { useState } from "react";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Button } from "@/components/ui/button";
import { CopyButton } from "@/components/ui/copy-button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { getConfig } from "@/lib/config";
import { formatDateTime } from "@/lib/format";

type CreateMagicLinkResponse = components["schemas"]["CreateMagicLinkResponse"];

interface CreateMagicLinkDialogProps {
  organisationId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}

export function CreateMagicLinkDialog({
  organisationId,
  open,
  onOpenChange,
  onCreated,
}: CreateMagicLinkDialogProps) {
  const [label, setLabel] = useState("");
  const [expiresInDays, setExpiresInDays] = useState("7");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CreateMagicLinkResponse | null>(null);
  const [magicLinkUrl, setMagicLinkUrl] = useState("");

  function resetForm() {
    setLabel("");
    setExpiresInDays("7");
    setSubmitting(false);
    setError(null);
    setResult(null);
    setMagicLinkUrl("");
  }

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      resetForm();
    }
    onOpenChange(nextOpen);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    const days = Number(expiresInDays);
    if (!Number.isInteger(days) || days < 1) {
      setError("Expires in days must be a positive integer.");
      setSubmitting(false);
      return;
    }

    const { data, error: apiError } = await api.POST("/magic-links", {
      body: {
        organisationId,
        label: label.trim() || null,
        expiresInDays: days,
      },
    });

    if (apiError) {
      setError("Failed to create magic link.");
      setSubmitting(false);
      return;
    }

    const config = await getConfig();
    setMagicLinkUrl(`${config.SANDBOX_FRONTEND_URL}/register?token=${data!.token}`);
    setResult(data!);
    setSubmitting(false);
    onCreated();
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        {result ? (
          <>
            <DialogHeader>
              <DialogTitle>Magic Link Created</DialogTitle>
              <DialogDescription>
                Share this URL with the user. It can only be used once.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 text-sm">
              {result.label && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Label</span>
                  <span>{result.label}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">Expires</span>
                <span>{formatDateTime(result.expiresAt)}</span>
              </div>
              <div className="space-y-1.5">
                <span className="text-muted-foreground">URL</span>
                <div className="bg-muted flex items-center gap-2 rounded-md p-2">
                  <code className="flex-1 break-all text-xs">{magicLinkUrl}</code>
                  <CopyButton value={magicLinkUrl} />
                </div>
              </div>
            </div>

            <DialogFooter showCloseButton />
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>Create Magic Link</DialogTitle>
              <DialogDescription>
                Generate a magic link for this organisation.
              </DialogDescription>
            </DialogHeader>

            <div className="mt-4 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="magic-link-label">Label (optional)</Label>
                <Input
                  id="magic-link-label"
                  placeholder="e.g. Onboarding invite"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="magic-link-expires">Expires in (days)</Label>
                <Input
                  id="magic-link-expires"
                  type="number"
                  min={1}
                  value={expiresInDays}
                  onChange={(e) => setExpiresInDays(e.target.value)}
                />
              </div>
              {error && <p className="text-destructive text-sm">{error}</p>}
            </div>

            <DialogFooter className="mt-6">
              <Button type="submit" disabled={submitting}>
                {submitting && <Spinner className="size-4" />}
                Create
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
